import React, { useMemo } from 'react';

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function getTotalPages(rowCount, rowsPerPage) {
	if (rowCount <= 0) {
		return 1;
	}

	return Math.ceil(rowCount / rowsPerPage);
}

function buildPageList(currentPage, totalPages) {
	if (totalPages <= 1) {
		return [1];
	}

	const delta = 2;
	const pages = [];

	for (let page = 1; page <= totalPages; page += 1) {
		if (
			page === 1
			|| page === totalPages
			|| (page >= currentPage - delta && page <= currentPage + delta)
		) {
			pages.push(page);
		}
	}

	const withEllipsis = [];
	let previousPage = null;

	pages.forEach((page) => {
		if (previousPage !== null) {
			if (page - previousPage === 2) {
				withEllipsis.push(previousPage + 1);
			} else if (page - previousPage > 2) {
				withEllipsis.push('ellipsis');
			}
		}

		withEllipsis.push(page);
		previousPage = page;
	});

	return withEllipsis;
}

function ChevronIcon({ direction }) {
	const paths = {
		first: 'M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z',
		prev: 'M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6z',
		next: 'M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z',
		last: 'M5.59 16.59L10.17 12 5.59 7.41 7 6l6 6-6 6zm8-12h2v12h-2z',
	};

	return (
		<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
			<path fill="currentColor" d={paths[direction]} />
		</svg>
	);
}

function TablePagination({
	rowsPerPage,
	rowCount,
	currentPage,
	onChangePage,
	onChangeRowsPerPage,
	paginationRowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
}) {
	const totalPages = getTotalPages(rowCount, rowsPerPage);
	const lastIndex = Math.min(currentPage * rowsPerPage, rowCount);
	const firstIndex = rowCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
	const isFirstPage = currentPage <= 1;
	const isLastPage = currentPage >= totalPages;
	const pageItems = useMemo(
		() => buildPageList(currentPage, totalPages),
		[currentPage, totalPages]
	);

	const rangeText = rowCount === 0
		? `0-0 of 0`
		: currentPage === totalPages
			? `${firstIndex}-${rowCount} of ${rowCount}`
			: `${firstIndex}-${lastIndex} of ${rowCount}`;

	const handleRowsPerPageChange = (event) => {
		onChangeRowsPerPage(Number(event.target.value), currentPage);
	};

	return (
		<nav className="rdt_Pagination telemetry-table-pagination" aria-label="Table pagination">
			<label className="telemetry-pagination-rows">
				<span>Rows per page:</span>
				<select
					value={rowsPerPage}
					onChange={handleRowsPerPageChange}
					aria-label="Rows per page"
				>
					{paginationRowsPerPageOptions.map((option) => (
						<option key={option} value={option}>
							{option}
						</option>
					))}
				</select>
			</label>

			<span className="telemetry-pagination-range">{rangeText}</span>

			<div className="telemetry-pagination-nav">
				<button
					type="button"
					className="telemetry-pagination-btn"
					onClick={() => onChangePage(1, rowCount)}
					disabled={isFirstPage}
					aria-label="First page"
				>
					<ChevronIcon direction="first" />
				</button>
				<button
					type="button"
					className="telemetry-pagination-btn"
					onClick={() => onChangePage(currentPage - 1, rowCount)}
					disabled={isFirstPage}
					aria-label="Previous page"
				>
					<ChevronIcon direction="prev" />
				</button>

				<div className="telemetry-pagination-pages" role="group" aria-label="Page numbers">
					{pageItems.map((item, index) => {
						if (item === 'ellipsis') {
							return (
								<span
									key={`ellipsis-${index}`}
									className="telemetry-pagination-ellipsis"
									aria-hidden="true"
								>
									…
								</span>
							);
						}

						const isActive = item === currentPage;

						return (
							<button
								key={item}
								type="button"
								className={`telemetry-pagination-page${isActive ? ' is-active' : ''}`}
								onClick={() => onChangePage(item, rowCount)}
								disabled={isActive}
								aria-label={`Page ${item}`}
								aria-current={isActive ? 'page' : undefined}
							>
								{item}
							</button>
						);
					})}
				</div>

				<button
					type="button"
					className="telemetry-pagination-btn"
					onClick={() => onChangePage(currentPage + 1, rowCount)}
					disabled={isLastPage}
					aria-label="Next page"
				>
					<ChevronIcon direction="next" />
				</button>
				<button
					type="button"
					className="telemetry-pagination-btn"
					onClick={() => onChangePage(totalPages, rowCount)}
					disabled={isLastPage}
					aria-label="Last page"
				>
					<ChevronIcon direction="last" />
				</button>
			</div>
		</nav>
	);
}

export default TablePagination;
